import fubIcon from '@forwardflow/assets/fub.png'
import useGetUserRole from '@forwardflow/hooks/customHooks/useGetUserRole'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import { useOrganizationUsers } from '@forwardflow/hooks/customHooks/organization'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { useOrganizationUsersFilter } from '@forwardflow/store/filter'
import { Button } from '@forwardflow/ui-v2'
import { Select, message } from 'antd'
import React, { useState } from 'react'

type InboxOperation = 'organization' | 'agent' | 'self' | null

const getUserFromLocalStorage = () => {
   try {
      const userStr = localStorage.getItem('user')
      if (!userStr) return null
      return JSON.parse(userStr)
   } catch {
      return null
   }
}

const agentDisplayName = (agent: any) =>
   [agent?.firstName, agent?.lastName].filter(Boolean).join(' ') ||
   agent?.email ||
   'Agent'

const ConnectionStatus = ({ installed }: { installed: boolean }) => (
   <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
         installed
            ? 'border-emerald-300/20 bg-emerald-300/10 text-emerald-200'
            : 'border-white/10 bg-white/[0.05] text-white/45'
      }`}
   >
      <span
         className={`h-1.5 w-1.5 rounded-full ${
            installed ? 'bg-emerald-300' : 'bg-white/30'
         }`}
      />
      {installed ? 'Installed' : 'Not installed'}
   </span>
)

const FubInboxIntegration = () => {
   const [selectedAgent, setSelectedAgent] = useState('')
   const [activeOperation, setActiveOperation] =
      useState<InboxOperation>(null)
   const { data: progressData, mutate: mutateProgress } = useSetupProgress()
   const { page: initialPage, limit } = useOrganizationUsersFilter()
   const [page, setPage] = useState(initialPage)
   const [allAgents, setAllAgents] = useState<any[]>([])
   const {
      data: agents,
      isLoading,
      mutate,
   } = useOrganizationUsers({
      page,
      limit,
      invitationStatus: 'accepted',
   })
   const { data: userRoleData, isLoading: roleLoading } = useGetUserRole()
   const localUser = getUserFromLocalStorage()
   const userEmail = localUser?.email

   const isOwner =
      userRoleData?.roles?.includes('Owner') ||
      userEmail === 'operator@forward-flow.com'
   const isOrgInstalled = !!progressData?.fubInboxAppData

   React.useEffect(() => {
      if (agents?.data) {
         setAllAgents((prev) =>
            page === 1 ? agents.data : [...prev, ...agents.data]
         )
      }
   }, [agents, page])

   const selectedAgentObj = allAgents.find(
      (agent) => agent?.email === selectedAgent
   )
   const currentAgentObj = allAgents.find(
      (agent) => String(agent?.email) === String(userEmail)
   )

   const handleOrgInstallUninstall = async () => {
      if (activeOperation) return
      setActiveOperation('organization')
      try {
         if (isOrgInstalled) {
            await OrganizationService.uninstallInboxApp(0)
            message.success('Uninstalled for organization successfully')
         } else {
            await OrganizationService.installInboxApp(0)
            message.success('Installed for organization successfully')
         }
         await mutateProgress()
      } catch (error) {
         message.error('Organization operation failed')
      } finally {
         setActiveOperation(null)
      }
   }

   const handleAgentInstallUninstall = async () => {
      if (!selectedAgentObj?.crmUserId || activeOperation) return
      setActiveOperation('agent')
      try {
         if (selectedAgentObj.fubInboxAppData) {
            await OrganizationService.uninstallInboxApp(
               selectedAgentObj.crmUserId
            )
            message.success('Uninstalled successfully')
         } else {
            await OrganizationService.installInboxApp(selectedAgentObj.crmUserId)
            message.success('Installed successfully')
         }
         await mutate()
      } catch (error) {
         message.error('Operation failed')
      } finally {
         setActiveOperation(null)
      }
   }

   const handleCurrentAgentInstallUninstall = async () => {
      const crmUserId = currentAgentObj?.crmUserId
      if (!crmUserId || activeOperation) return
      setActiveOperation('self')
      try {
         if (currentAgentObj?.fubInboxAppData) {
            await OrganizationService.uninstallInboxApp(crmUserId)
            message.success('Uninstalled successfully')
         } else {
            await OrganizationService.installInboxApp(crmUserId)
            message.success('Installed successfully')
         }
         await mutate()
      } catch (error) {
         message.error('Operation failed')
      } finally {
         setActiveOperation(null)
      }
   }

   const handlePopupScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
      const target = e.target as HTMLDivElement
      if (
         !isLoading &&
         agents?.data?.length === limit &&
         target.scrollTop + target.offsetHeight >= target.scrollHeight - 50
      ) {
         setPage((prev = 1) => prev + 1)
      }
   }

   if (roleLoading) {
      return (
         <div className="mt-6 space-y-4 rounded-2xl border border-white/10 bg-white/[0.035] p-6">
            <div className="h-5 w-52 animate-pulse rounded bg-white/10" />
            <div className="h-4 w-4/5 animate-pulse rounded bg-white/[0.07]" />
            <div className="h-20 animate-pulse rounded-xl bg-white/[0.05]" />
         </div>
      )
   }

   return (
      <section className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035] shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
         <div className="flex flex-col gap-3 border-b border-white/10 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
            <div className="flex items-start gap-4">
               <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white">
                  <img src={fubIcon} alt="" className="h-8 w-8" />
               </span>
               <div>
                  <div className="flex flex-wrap items-center gap-2">
                     <h2 className="font-medium text-white">
                        Follow Up Boss Inbox app
                     </h2>
                     {isOwner && <ConnectionStatus installed={isOrgInstalled} />}
                  </div>
                  <p className="mt-1 max-w-xl text-sm leading-6 text-white/60">
                     Install the Forward Flow app in Follow Up Boss so messages and actions are available directly from the lead inbox.
                  </p>
               </div>
            </div>
         </div>

         {isOwner ? (
            <div className="p-5 sm:p-6">
               <div className="flex flex-col gap-4 rounded-xl border border-white/[0.08] bg-black/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                     <p className="text-sm font-medium text-white">
                        Organization-wide installation
                     </p>
                     <p className="mt-1 text-xs leading-5 text-white/45">
                        {isOrgInstalled
                           ? 'The Inbox app is available across this organization.'
                           : 'Install once to make the Inbox app available to the whole organization.'}
                     </p>
                  </div>
                  <Button
                     variant={isOrgInstalled ? 'secondary' : 'primary'}
                     className="!h-10 shrink-0 !rounded-lg px-4 text-sm"
                     onClick={handleOrgInstallUninstall}
                     loading={activeOperation === 'organization'}
                     disabled={!!activeOperation}
                  >
                     {isOrgInstalled
                        ? 'Uninstall for organization'
                        : 'Install for organization'}
                  </Button>
               </div>

               <div className="mt-6 border-t border-white/10 pt-6">
                  <div className="mb-4">
                     <p className="text-sm font-medium text-white">
                        Manage an individual agent
                     </p>
                     <p className="mt-1 text-xs leading-5 text-white/45">
                        Choose an accepted agent, then install or remove the Inbox app for that person.
                     </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                     <div className="min-w-0">
                        <label
                           htmlFor="fub-agent-select"
                           className="mb-2 block text-xs font-medium text-white/65"
                        >
                           Agent
                        </label>
                        <Select
                           id="fub-agent-select"
                           className="w-full [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-white/15 [&_.ant-select-selector]:!bg-white/[0.06] [&_.ant-select-selection-item]:!text-white"
                           value={selectedAgent || undefined}
                           onChange={(value) => setSelectedAgent(value)}
                           placeholder="Search or select an agent"
                           optionFilterProp="children"
                           showSearch
                           dropdownStyle={{ minWidth: '240px' }}
                           popupClassName="text-black"
                           onPopupScroll={handlePopupScroll}
                           loading={isLoading}
                           disabled={!!activeOperation}
                        >
                           {allAgents.map((agent) => (
                              <Select.Option
                                 key={agent?.email}
                                 value={agent?.email}
                              >
                                 {agentDisplayName(agent)} ({agent.email})
                              </Select.Option>
                           ))}
                        </Select>
                     </div>
                     <Button
                        variant={
                           selectedAgentObj?.fubInboxAppData
                              ? 'secondary'
                              : 'primary'
                        }
                        className="!h-10 !rounded-lg px-5 text-sm"
                        onClick={handleAgentInstallUninstall}
                        loading={activeOperation === 'agent'}
                        disabled={!selectedAgentObj?.crmUserId || !!activeOperation}
                     >
                        {selectedAgentObj?.fubInboxAppData
                           ? 'Uninstall for agent'
                           : 'Install for agent'}
                     </Button>
                  </div>

                  {selectedAgentObj && (
                     <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/[0.035] px-3 py-2">
                        <span className="min-w-0 truncate text-xs text-white/55">
                           {agentDisplayName(selectedAgentObj)} · {selectedAgentObj.email}
                        </span>
                        <ConnectionStatus
                           installed={!!selectedAgentObj.fubInboxAppData}
                        />
                     </div>
                  )}
               </div>
            </div>
         ) : (
            <div className="p-5 sm:p-6">
               <div className="flex flex-col gap-4 rounded-xl border border-white/[0.08] bg-black/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                     <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-white">
                           Your Inbox app
                        </p>
                        <ConnectionStatus
                           installed={!!currentAgentObj?.fubInboxAppData}
                        />
                     </div>
                     <p className="mt-1 truncate text-xs text-white/45">
                        {userEmail || 'Your Follow Up Boss agent account'}
                     </p>
                  </div>
                  <Button
                     variant={
                        currentAgentObj?.fubInboxAppData
                           ? 'secondary'
                           : 'primary'
                     }
                     className="!h-10 shrink-0 !rounded-lg px-5 text-sm"
                     onClick={handleCurrentAgentInstallUninstall}
                     loading={activeOperation === 'self'}
                     disabled={!currentAgentObj?.crmUserId || !!activeOperation}
                  >
                     {currentAgentObj?.fubInboxAppData
                        ? 'Uninstall app'
                        : 'Install app'}
                  </Button>
               </div>
            </div>
         )}
      </section>
   )
}

export default FubInboxIntegration
